'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Container } from '@/components/ui/Container';
import { Heading } from '@/components/ui/Heading';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { normalizePartnerName } from '@/lib/image-alt-helper';
import { type Locale } from '@/lib/i18n';
import styles from './IntegrationsSection.module.css';

interface Integration {
  name: string;
  logo: string;
  slug: string;
}

interface IntegrationsSectionProps {
  translations: {
    label: string;
    title: string;
    description: string;
    cta?: string;
  };
  altTexts?: Record<string, string>;
  background?: 'primary' | 'secondary';
  locale?: Locale;
  ctaId?: string;
  ctaPosition?: string;
}

const integrations: Integration[] = [
  { name: 'Lightspeed', logo: '/images/integrations/pos/lightspeed.webp', slug: 'lightspeed' },
  { name: 'unTill', logo: '/images/integrations/pos/untill.webp', slug: 'untill' },
  { name: 'Oracle', logo: '/images/integrations/pos/oracle.webp', slug: 'oracle' },
  { name: 'Deliverect', logo: '/images/integrations/pos/deliverect.webp', slug: 'deliverect' },
  { name: 'MplusKASSA', logo: '/images/integrations/pos/mpluskassa.webp', slug: 'mpluskassa' },
  { name: 'Trivec', logo: '/images/integrations/pos/trivec.webp', slug: 'trivec' },
  { name: 'Gastrofix', logo: '/images/integrations/pos/gastrofix.webp', slug: 'gastrofix' },
  { name: 'NuvoPOS', logo: '/images/integrations/pos/nuvopos.webp', slug: 'nuvopos' },
  { name: 'Vectron', logo: '/images/integrations/pos/vectron.webp', slug: 'vectron' },
  { name: 'Twelve', logo: '/images/integrations/pos/twelve.webp', slug: 'twelve' },
  { name: 'NCR Aloha', logo: '/images/integrations/pos/ncr-aloha.webp', slug: 'ncr-aloha' },
  { name: 'CashDesk', logo: '/images/integrations/pos/cashdesk.webp', slug: 'cashdesk' },
  { name: 'Mr. Winston', logo: '/images/integrations/pos/mr-winston.webp', slug: 'mr-winston' },
  { name: 'Hypersoft', logo: '/images/integrations/pos/hypersoft.webp', slug: 'hypersoft' },
];

export default function IntegrationsSection({ 
  translations, 
  altTexts,
  background = 'primary',
  locale = 'en',
  ctaId = 'cta_integrations',
  ctaPosition = 'after_integrations'
}: IntegrationsSectionProps) {
  // Helper function to get localized integration URL
  const getIntegrationUrl = (slug: string): string => {
    const pathMap: Record<Locale, string> = {
      en: `/integrations/${slug}`,
      nl: `/nl/integraties/${slug}`,
      de: `/de/integrationen/${slug}`,
    };
    return pathMap[locale];
  };

  return (
    <Section background={background} className={styles.integrationsSection}>
      <Container>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>{translations.label}</span>
          <Heading as="h2" className={styles.sectionTitle}>
            {translations.title}
          </Heading>
          <Text size="lg" color="secondary" className={styles.integrationsDescription}>
            {translations.description}
          </Text>
        </div>

        <div className={styles.integrationsGrid}>
          {integrations.map((partner, index) => (
            <Link
              href={getIntegrationUrl(partner.slug)}
              key={index} 
              className={styles.integrationCard}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className={styles.integrationLogoWrapper}>
                <Image
                  src={partner.logo}
                  alt={altTexts?.[normalizePartnerName(partner.name)] || `${partner.name} integration`}
                  width={200}
                  height={100}
                  className={styles.integrationLogo}
                  style={{ objectFit: 'contain' }}
                  loading={index < 8 ? 'eager' : 'lazy'}
                />
              </div>
              <span className={styles.integrationName}>{partner.name}</span>
            </Link>
          ))}
        </div>

        {translations.cta && (
          <div className={styles.ctaWrapper}>
            <Link href={locale === 'en' ? '/integrations' : locale === 'nl' ? '/nl/integraties' : '/de/integrationen'}>
              <Button
                variant="primary"
                size="lg"
                data-cta-id={ctaId}
                data-cta-position={ctaPosition}
                data-cta-variant="primary"
              >
                {translations.cta}
              </Button>
            </Link>
          </div>
        )}
      </Container>
    </Section>
  );
}

